import { deletePartnerFlagRequest } from 'legacy/lut/services/partner/partnerFlags'
import { memo, useCallback, useEffect, useMemo, useState } from 'react'

import { OnboardingResponseStatus } from 'clickNgo/configs/service/onboarding'
import {
  createPartnerFlagRequest,
  updatePartnerFlagRequest,
} from 'clickNgo/entities/partner/api/partnerFlags'
import { mapPartnerFlagFromRequest } from 'clickNgo/entities/partner/api/partnerFlags/mapping'
import {
  IPartnerFlag,
  PartnerFlagRequestAction,
} from 'clickNgo/entities/partner/models/partnerFlag'
import { PartnerFlagRequest } from 'clickNgo/features/partnerFlagRequest'
import { formatErrorMessages, getJSendErrorMessages } from 'clickNgo/helpers/api'
import { InfoBlock, InfoBlockType } from 'cng-common/components/infoBlock'
import { RenderWhen } from 'cng-common/components/renderWhen'
import { LoaderOverlay } from 'core-components/library/loader'
import { ModalDialog } from 'core-components/library/modalDialog'
import { List } from 'shared/components/list'

import { EmptyListItem } from './components/emptyListItem/EmptyListItem'
import { PartnerFlag } from './components/partnerFlag/PartnerFlag'
import { PartnerFlagActions } from './components/partnerFlagActions/PartnerFlagActions'
import { defaultAsyncFn } from './helpers/defaultAsyncFn'
import { filterPartnerFlagList } from './helpers/filterPartnerFlagList'
import { IPartnerFlagsProps } from './models'

import styles from './PartnerFlags.module.scss'

export const PartnerFlags = memo<IPartnerFlagsProps>(
  ({
    type,
    partner,
    partnerFlags,
    isDisabled,
    isReadOnly,
    onCreate = defaultAsyncFn,
    onDelete = defaultAsyncFn,
    onUpdate = defaultAsyncFn,
    onError = defaultAsyncFn,
  }) => {
    /** ------------------------ Variables ------------------------ */
    const [isLoading, setIsLoading] = useState(false)
    const [isPartnerFlagRequestVisible, setIsPartnerFlagRequestVisible] = useState(false)
    const [searchKey, setSearchKey] = useState('')
    const currentTypePartnerFlags = partnerFlags.filter(({ codeType }) => codeType === type)
    const [filteredPartnerFlagList, setFilteredPartnerFlagList] =
      useState<IPartnerFlag[]>(currentTypePartnerFlags)
    const [currentExpandedFlag, setCurrentExpandedFlag] = useState('')
    const [errorMessages, setErrorMessages] = useState<string[]>([])

    const formattedErrorMessages = useMemo(
      () => formatErrorMessages(errorMessages),
      [errorMessages]
    )

    /** ---------------- Handlers & Render methods ---------------- */

    const handleOnFlagClick = useCallback(
      (itemName: string) => {
        if (currentExpandedFlag === itemName) {
          setCurrentExpandedFlag('')
        } else {
          setCurrentExpandedFlag(itemName)
        }
      },
      [currentExpandedFlag]
    )

    const handleOnAddButtonClick = useCallback(() => {
      setIsPartnerFlagRequestVisible(true)
    }, [])

    const handleOnAddNewPartnerFlagConfirm = useCallback(
      async (partnerFlag: IPartnerFlag) => {
        setErrorMessages([])
        setIsLoading(true)
        setIsPartnerFlagRequestVisible(false)
        const response = await createPartnerFlagRequest(partnerFlag)
        setIsLoading(false)

        if (response.status === OnboardingResponseStatus.Fail) {
          setErrorMessages(getJSendErrorMessages(response))
          return onError(partnerFlag)
        }

        if (response.status === OnboardingResponseStatus.Error) {
          setErrorMessages([response.message])
          return onError(partnerFlag)
        }

        if (response.status === 'success') {
          const newPartnerFlag = mapPartnerFlagFromRequest(response.data)
          return onCreate(newPartnerFlag)
        }
      },
      [onCreate, onError]
    )

    const handleOnAddNewPartnerFlagReject = useCallback(() => {
      setIsPartnerFlagRequestVisible(false)
    }, [])

    const handleOnUpdatePartnerFlag = useCallback(
      async (partnerFlag: IPartnerFlag) => {
        setErrorMessages([])
        setIsLoading(true)
        const isNewRequest = Boolean(!partnerFlag.status)

        const currentDate = new Date()
        const isDeleteAction =
          partnerFlag.endDate.setHours(0, 0, 0, 0) <= currentDate.setHours(0, 0, 0, 0)

        const action = isDeleteAction
          ? PartnerFlagRequestAction.Remove
          : PartnerFlagRequestAction.Modify

        const updateFunction = isNewRequest ? createPartnerFlagRequest : updatePartnerFlagRequest
        const response = await updateFunction({ ...partnerFlag, action })
        setIsLoading(false)

        if (response.status === OnboardingResponseStatus.Fail) {
          setErrorMessages(getJSendErrorMessages(response))
          return onError(partnerFlag)
        }

        if (response.status === OnboardingResponseStatus.Error) {
          setErrorMessages([response.message])
          return onError(partnerFlag)
        }

        if (response.status === 'success') {
          const newPartnerFlag = mapPartnerFlagFromRequest(response.data)
          return onUpdate(newPartnerFlag)
        }
      },
      [onError, onUpdate]
    )

    const handleOnDeletePartnerFlag = useCallback(
      (partnerFlag: IPartnerFlag) => {
        setErrorMessages([])
        setIsLoading(true)
        deletePartnerFlagRequest(partnerFlag.entryId)
        onDelete(partnerFlag).finally(() => {
          setIsLoading(false)
        })
      },
      [onDelete]
    )

    const renderPartnerFlagRequestContent = useCallback(() => {
      return (
        <PartnerFlagRequest
          onApply={handleOnAddNewPartnerFlagConfirm}
          onClose={handleOnAddNewPartnerFlagReject}
          partner={partner}
          partnerFlags={partnerFlags}
          type={type}
        />
      )
    }, [
      handleOnAddNewPartnerFlagConfirm,
      handleOnAddNewPartnerFlagReject,
      partner,
      partnerFlags,
      type,
    ])

    /** ---------------- Render methods ---------------- */

    const renderListItem = useCallback(() => {
      const itemList = filteredPartnerFlagList.map((partnerFlag, index) => (
        <PartnerFlag
          isDisabled={isDisabled}
          isReadOnly={isReadOnly}
          key={`${partnerFlag.entryId}-${index}`}
          partnerFlag={partnerFlag}
          onClick={handleOnFlagClick}
          currentExpandedFlag={currentExpandedFlag}
          onDelete={handleOnDeletePartnerFlag}
          onUpdate={handleOnUpdatePartnerFlag}
        />
      ))

      const emptyList = <EmptyListItem key="emptyList" />

      if (filteredPartnerFlagList.length) {
        return itemList
      } else {
        return [emptyList]
      }
    }, [
      currentExpandedFlag,
      filteredPartnerFlagList,
      handleOnDeletePartnerFlag,
      handleOnFlagClick,
      handleOnUpdatePartnerFlag,
      isDisabled,
      isReadOnly,
    ])

    /** ------------------------- Effects ------------------------- */

    useEffect(() => {
      setCurrentExpandedFlag('')
      const newPartnerFlagList = partnerFlags
        .filter(({ codeType }) => codeType === type)
        .filter((el) => filterPartnerFlagList(el, searchKey))
        .sort((prev, next) => (next.endDate > prev.endDate ? -1 : 1))
        .sort((el) => (el.status ? -1 : 1))
      setFilteredPartnerFlagList(newPartnerFlagList)
    }, [searchKey, partnerFlags, type])

    return (
      <div className={styles.container}>
        <LoaderOverlay show={isLoading} />
        <RenderWhen condition={Boolean(errorMessages?.length)}>
          <InfoBlock text={formattedErrorMessages} type={InfoBlockType.Danger} hasCloseButton />
        </RenderWhen>
        <PartnerFlagActions
          key="actionPanel"
          isDisabled={isDisabled}
          isReadOnly={isReadOnly}
          onSearchKeyChange={setSearchKey}
          onAddButtonClick={handleOnAddButtonClick}
        />
        <List className={styles.list}>{renderListItem()}</List>
        <ModalDialog
          classes={{ ...styles }}
          show={isPartnerFlagRequestVisible && !isLoading}
          content={renderPartnerFlagRequestContent}
          onClose={handleOnAddNewPartnerFlagReject}
          closeOnOutsideClick={false}
          size="sm"
        />
      </div>
    )
  }
)

PartnerFlags.displayName = 'PartnerFlags'
